/**
 * auth/AuthContext.tsx — Local access-code session state
 * ======================================================
 *
 * The dashboard uses a static code-to-sheet map.
 * Each access code unlocks one sheet; the admin code unlocks all sheets.
 *
 * Session persistence: the active access code and selected sheet ID are
 * stored in localStorage so that page refreshes do not require re-login.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { SheetSource, UserAccess } from '@/config/sheets';
import type { StateCode } from '@/tariffs';
import type { MeterModelCode } from '@/meters';
import CryptoJS from 'crypto-js';
import { getClientId } from '@/config/tenant';
import { signInWithEmailAndPassword, signOut as fbSignOut } from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';

export interface AuthUser {
  email:          string;
  name:           string;
  access:         UserAccess;
}

interface AuthContextValue {
  user:              AuthUser | null;
  status:            'idle' | 'authenticating' | 'authenticated' | 'denied';
  error:             string | null;
  selectedSheet:     SheetSource | null;
  setSelectedSheet:  (sheet: SheetSource) => void;
  signIn:            (code: string) => Promise<void>;
  signOut:           () => Promise<void>;
  /** True once Firebase Auth sign-in has completed. useSheetData waits for
   *  this before opening the Firestore listener to prevent permission errors
   *  on page refresh (when the session is restored before Auth is re-established). */
  firebaseAuthReady: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type LocalSheetDefinition = SheetSource & {
  codeHash: string;
  displayName: string;
};

const LOCAL_SHEETS: LocalSheetDefinition[] = [
  {
    codeHash: '895020aa368038952fa965778b412586999e433fa7084cf8c2cef4911a63585a',
    id: 'raltech-main',
    label: 'RALTECH · Main',
    sheetId: '1OtOA2hk7I55s0DeY6EnqPbG2C-bMynhb2Zm646FPSP4',
    gid: '1219931086',
    stateCode: 'telangana' as StateCode,
    meterModel: 'lk4410' as MeterModelCode,
    dashboardAuth: { email: 'raltech-main-dashboard@vireon.internal', password: 'zHFhryO15SByHhm3ckhTvhkW' },
    // ACD from the SGR2985 invoice (RALPRO Techno Crafts, CD 1501). Update once
    // per deposit year, or set 0 when no ACD is due for the cycle being viewed.
    meta: { location: 'Telangana', tariffCode: 'HT Cat. 1A', contractDemandKva: 1501, acdSurchargeRs: 48736 },
    displayName: 'RALTECH', tenantId: 'raltech',
  },
  {
    codeHash: '8ec19450937e593960b154ffcabd904ab9f1b58f87a30d5333dd214441d3942d',
    id: 'ralpro-main',
    label: 'RALPRO · Main',
    sheetId: '1FKVTj_ib_BqBjymE1nE84KlORg4VvpTiBquxEfFFD8U',
    gid: '1124232844',
    stateCode: 'telangana' as StateCode,
    meterModel: 'lk4410' as MeterModelCode,
    dashboardAuth: { email: 'ralpro-main-dashboard@vireon.internal', password: 'VDoSWa7oTlqL2bRuldIuq6Zs' },
    meta: { location: 'Telangana', tariffCode: 'HT Cat. 1A', contractDemandKva: 2501 },
    displayName: 'RALPRO', tenantId: 'ralpro',
  },
  {
    codeHash: 'f11f4ed58b789f5217b9b24cd7637d82ccb10f2d96985e0e73502355490aeaf8',
    id: 'omega-main',
    label: 'OMEGA · Main',
    sheetId: '1CAa2dpO_wuBVWkMabplzrRVEaAJkZYUOgVE-KzD8A0w',
    gid: '1627567836',
    stateCode: 'west-bengal' as StateCode,
    meterModel: 'lk4405' as MeterModelCode,
    dashboardAuth: { email: 'omega-main-dashboard@vireon.internal', password: 'zBa6iFDDIOFaycjtL2sC-boM' },
    meta: { location: 'West Bengal', tariffCode: 'B-IDIT', contractDemandKva: 160 },
    displayName: 'OMEGA', tenantId: 'omega',
  },
  {
    codeHash: 'd4806994654ffffe585820b330008c815b14f0741cecaeb070ea4c3ee92e249d',
    id: 'taparia-main',
    label: 'TAPARIA · Main',
    sheetId: '1ya7K7JY75mT7FuS1fjzvhfC8LgpJctvdw8LTWyG3Quw',
    gid: '686617454',
    stateCode: 'west-bengal' as StateCode,
    meterModel: 'lk4405' as MeterModelCode,
    dashboardAuth: { email: 'taparia-main-dashboard@vireon.internal', password: 'WrPcmz7aKcRsgiagzjtf8qB6' },
    meta: { location: 'West Bengal', tariffCode: 'B-IDIT', contractDemandKva: 176 },
    displayName: 'TAPARIA', tenantId: 'taparia',
  },
  {
    codeHash: '14e300a2ae380000c9837f5522a56e8026d16d7ea8de7efc68f59ff25f52f3d9',
    id: 'Zip-infra',
    label: 'Zip-infra · Main',
    sheetId: '1yDtkG2xCZ856TsmYJ4-NSk-7we-BWKcZ4p3J7pTp2uM',
    gid: '1920334454',
    stateCode: 'west-bengal' as StateCode,
    meterModel: 'lk4410' as MeterModelCode,
    // No dashboardAuth — Zip-infra has no Firebase dashboard identity yet.
    meta: { location: 'West Bengal', tariffCode: 'B-IDIT', contractDemandKva: 160 },
    displayName: 'Zip-infra', tenantId: 'zipinfra',
  },
];

// SHA-256 of the admin master code. Raw code is not present in the bundle.
const ADMIN_CODE_HASH = '62c64bfa2d9b44d7a8262e725cb047c061eacba6506b847db20ab646f11b99b2';

const ADMIN_DASHBOARD_AUTH = { email: 'admin@vireon.internal', password: 'KF5aIndiflWI4Hdo-6GrzgIV' };

/** Signs the dashboard identity into Firebase Auth so Firestore rules pass.
 *  Non-blocking: if this fails the error is logged but the session continues. */
async function signInToFirestoreAuth(
  creds: { email: string; password: string } | undefined,
): Promise<void> {
  if (!creds) return;
  try {
    await signInWithEmailAndPassword(getFirebaseAuth(), creds.email, creds.password);
  } catch (err) {
    console.error('[Cortex] Firebase Auth sign-in failed:', err instanceof Error ? err.message : err);
  }
}

function getAllowedSheets(): LocalSheetDefinition[] {
  const clientId = getClientId().toLowerCase();
  if (clientId === 'master') {
    return LOCAL_SHEETS;
  }
  return LOCAL_SHEETS.filter(sheet => sheet.tenantId === clientId || sheet.id.toLowerCase().startsWith(clientId));
}

/**
 * SHA-256 hex via Web Crypto. Client-side hashing keeps raw codes out of the
 * bundle and out of localStorage; it is NOT a substitute for server-side auth.
 * The Sheet IDs still ship to the client (the GViz fetch is client-side); a
 * production build should validate codes in an edge function and return only a
 * short-lived signed token plus the entitled Sheet IDs.
 */
async function sha256Hex(input: string): Promise<string> {
  return CryptoJS.SHA256(input).toString(CryptoJS.enc.Hex);
}

// ─── localStorage persistence keys ────────────────────────────────────────────
const LS_CODE_KEY  = 'cortex_session_code';
const LS_SHEET_KEY = 'cortex_session_sheet_id';

function toSheetSource(def: LocalSheetDefinition): SheetSource {
  return {
    id: def.id,
    label: def.label,
    sheetId: def.sheetId,
    gid: def.gid,
    stateCode: def.stateCode,
    meterModel: def.meterModel,
    meta: def.meta,
  };
}

function buildUser(defs: LocalSheetDefinition[], email: string, role: UserAccess['role']): AuthUser {
  const sheets = defs.map(toSheetSource);
  const displayName = defs[0]?.displayName ?? email;

  return {
    email,
    name: displayName,
    access: {
      email,
      password: '',
      displayName,
      role,
      sheets,
    },
  };
}

/** Attempt to restore a session from localStorage. Returns null if nothing saved.
 *  LS_CODE_KEY stores the SHA-256 HASH of the code (never the raw code), so this
 *  synchronous compare needs no hashing. */
function restoreSession(): { user: AuthUser; sheet: SheetSource } | null {
  try {
    const savedHash    = localStorage.getItem(LS_CODE_KEY)?.trim().toLowerCase();
    const savedSheetId = localStorage.getItem(LS_SHEET_KEY);
    if (!savedHash) return null;

    const allowedSheets = getAllowedSheets();
    if (allowedSheets.length === 0) return null;

    if (savedHash === ADMIN_CODE_HASH) {
      const authUser = buildUser(allowedSheets, 'admin@cortex.internal', 'admin');
      const sheet = (savedSheetId
        ? authUser.access.sheets.find(s => s.id === savedSheetId)
        : null) ?? authUser.access.sheets[0] ?? null;
      if (!sheet) return null;
      return { user: authUser, sheet };
    }

    const def = allowedSheets.find(item => item.codeHash === savedHash);
    if (!def) return null;

    const authUser = buildUser([def], `${def.id}@cortex.internal`, 'viewer');
    const sheet = authUser.access.sheets[0] ?? null;
    if (!sheet) return null;
    return { user: authUser, sheet };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Restore session synchronously from localStorage before first render
  const restored = useMemo(() => restoreSession(), []);

  const [user,               setUser]               = useState<AuthUser | null>(restored?.user ?? null);
  const [status,             setStatus]             = useState<AuthContextValue['status']>(
    restored ? 'authenticated' : 'idle',
  );
  const [error,              setError]              = useState<string | null>(null);
  const [selectedSheet,      setSelectedSheetState] = useState<SheetSource | null>(restored?.sheet ?? null);
  const [firebaseAuthReady,  setFirebaseAuthReady]  = useState<boolean>(false);

  // Re-establish Firebase Auth after a localStorage-restored session.
  // restoreSession() sets selectedSheet synchronously before first render,
  // so useSheetData's effect fires immediately — before signIn() has had any
  // chance to call signInToFirestoreAuth(). Without this effect, every page
  // refresh results in a Firestore "permission denied" error because the
  // listener opens before Firebase Auth has a signed-in identity.
  useEffect(() => {
    if (restored) {
      const isAdmin = restored.user.access.role === 'admin';
      const creds = isAdmin ? ADMIN_DASHBOARD_AUTH : restored.sheet.dashboardAuth;
      void signInToFirestoreAuth(creds).then(() => setFirebaseAuthReady(true));
    } else {
      setFirebaseAuthReady(true); // no session to restore — nothing to wait for
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Persist selected sheet ID whenever it changes
  const setSelectedSheet = useCallback((sheet: SheetSource) => {
    setSelectedSheetState(sheet);
    try { localStorage.setItem(LS_SHEET_KEY, sheet.id); } catch { /* ignore */ }
  }, []);

  // Clear persistence on sign-out
  useEffect(() => {
    if (status === 'idle') {
      try {
        localStorage.removeItem(LS_CODE_KEY);
        localStorage.removeItem(LS_SHEET_KEY);
      } catch { /* ignore */ }
    }
  }, [status]);

  const signIn = useCallback(async (code: string) => {
    const normalized = code.trim().toLowerCase();
    setStatus('authenticating');
    setError(null);

    if (!normalized) {
      setStatus('denied');
      setError('Please enter an access code.');
      return;
    }

    const entryHash = await sha256Hex(normalized);
    const allowedSheets = getAllowedSheets();

    if (allowedSheets.length === 0) {
      setUser(null);
      setSelectedSheetState(null);
      setStatus('denied');
      setError('This tenant has no configured data streams.');
      return;
    }

    if (entryHash === ADMIN_CODE_HASH) {
      const authUser = buildUser(allowedSheets, 'admin@cortex.internal', 'admin');
      const sheet = authUser.access.sheets[0] ?? null;
      await signInToFirestoreAuth(ADMIN_DASHBOARD_AUTH);
      setUser(authUser);
      setSelectedSheetState(sheet);
      setStatus('authenticated');
      setFirebaseAuthReady(true);
      try {
        localStorage.setItem(LS_CODE_KEY, entryHash);
        if (sheet) localStorage.setItem(LS_SHEET_KEY, sheet.id);
      } catch { /* ignore */ }
      return;
    }

    const def = allowedSheets.find((item) => item.codeHash === entryHash);
    if (!def) {
      setUser(null);
      setSelectedSheetState(null);
      setStatus('denied');
      setError('Invalid access code for this workspace. Please check and try again.');
      return;
    }

    const authUser = buildUser([def], `${def.id}@cortex.internal`, 'viewer');
    const sheet = authUser.access.sheets[0] ?? null;
    await signInToFirestoreAuth(def.dashboardAuth);
    setUser(authUser);
    setSelectedSheetState(sheet);
    setStatus('authenticated');
    setFirebaseAuthReady(true);
    try {
      localStorage.setItem(LS_CODE_KEY, entryHash);
      if (sheet) localStorage.setItem(LS_SHEET_KEY, sheet.id);
    } catch { /* ignore */ }
  }, []);

  const signOut = useCallback(async () => {
    setUser(null);
    setSelectedSheetState(null);
    setError(null);
    setStatus('idle');
    setFirebaseAuthReady(false);
    fbSignOut(getFirebaseAuth()).catch(() => {});
    // localStorage cleared by the useEffect above
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    status,
    error,
    selectedSheet,
    setSelectedSheet,
    signIn,
    signOut,
    firebaseAuthReady,
  }), [user, status, error, selectedSheet, setSelectedSheet, signIn, signOut, firebaseAuthReady]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
